import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { AccountRecord, useCheckMetaAuthorisationForAccountQuery } from '../../api/client'
import { Link } from 'react-router-dom'

type Props = {
    account: AccountRecord
    // When true, render a single tick/cross icon with a short "auth" label and a
    // tooltip — for at-a-glance status in the account list. Full labelled detail
    // (default) is shown in the expandable admin row.
    compact?: boolean
}

function AccountAuthorisationStatus(props: Props) {
    const { account, compact } = props

    const { data, loading, error } = useCheckMetaAuthorisationForAccountQuery({
        variables: { accountId: account.id }
    })

    if (compact) {
        if (loading) return <span className="text-[11px] text-ink-400">auth…</span>
        const ok = !error && data?.checkMetaAuthorisationForAccount
        return (
            <span
                className="inline-flex items-center gap-1 text-[11px]"
                title={
                    error
                        ? 'Error checking authorisation status'
                        : ok
                          ? 'Has valid authorisation'
                          : 'Authorisation invalid'
                }
            >
                {ok ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
                ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
                )}
                <span className={ok ? 'text-ink-400' : 'text-red-600'}>auth</span>
            </span>
        )
    }

    if (loading) return <div>Checking authorisation status...</div>
    if (error)
        return (
            <div className="text-red-500">
                Error checking authorisation status
                <Link
                    className={`text-arwen-600 p-2 underline `}
                    to={`/account/${account.id}/settings/validation`}
                >
                    Click here to try again
                </Link>
            </div>
        )

    const isValid = data?.checkMetaAuthorisationForAccount

    return (
        <>
            <div className="flex items-center space-x-2 justify-center">
                {isValid ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" aria-hidden="true" />
                ) : (
                    <XCircleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
                )}
                <span className="text-gray-700">
                    {isValid ? 'Has valid authorisation' : 'Authorisation invalid'}
                </span>
            </div>
        </>
    )
}

export default AccountAuthorisationStatus
